<script>
  import { link } from "svelte-routing";
  import FooterAdmin from "../components/Footers/FooterAdmin.svelte";
  import Logo from "../components/Logo.svelte";
</script>

<div class="bg-canvas min-h-screen">
  <!-- Simple Navbar -->
  <nav class="fixed top-0 z-50 w-full bg-surface border-b border-line">
    <div class="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
      <a use:link href="/" aria-label="Moira Finance home">
        <Logo />
      </a>
      <div class="flex items-center gap-2">
        <a use:link href="/terms" class="btn btn-ghost btn-sm">Terms</a>
        <a use:link href="/privacy" class="btn btn-ghost btn-sm">Privacy</a>
        <a use:link href="/auth/login" class="btn btn-outline btn-sm">
          Sign in
        </a>
      </div>
    </div>
  </nav>

  <main class="max-w-3xl mx-auto px-4 pt-28 pb-16">
    <h1 class="font-display text-4xl font-medium tracking-tight text-ink">Terms of Service</h1>
    <p class="text-sm text-ink-tertiary mt-2">Last Updated: December 2024</p>

    <div class="text-base text-ink-secondary leading-relaxed">

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">1. Agreement to Terms</h2>
        <p class="mb-4">
          By accessing or using Moira Finance ("Service," "we," "our," or "us"), you agree to be bound by these
          Terms of Service ("Terms"). If you disagree with any part of the terms, you do not have permission
          to access the Service.
        </p>
        <p>
          These Terms apply to all visitors, users, and others who access or use the Service. By using the
          Service, you represent that you are at least 18 years of age and have the legal capacity to enter
          into a binding agreement.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">2. Description of Service</h2>
        <p class="mb-4">
          Moira Finance is a technology platform that provides tools and interfaces for automated portfolio
          management and investment services. The actual securities brokerage services are provided by
          Alpaca Securities LLC ("Alpaca Securities"), a registered broker-dealer and member of FINRA/SIPC.
        </p>
        <p class="mb-4">
          <strong class="text-ink">Important:</strong> Moira Finance is not a registered broker-dealer, investment advisor,
          or financial planner. We do not provide investment advice, tax advice, legal advice, or
          recommendations regarding the suitability of any investments.
        </p>
        <p class="mb-3">
          The Service includes, but is not limited to:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Portfolio creation and management tools ("Pies")</li>
          <li>Automated investment and rebalancing features</li>
          <li>Account information and performance tracking</li>
          <li>Bank account linking and fund transfer interfaces</li>
          <li>Research and market data display</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">3. Brokerage Services</h2>
        <p class="mb-4">
          Securities brokerage services are provided by Alpaca Securities LLC, member
          <a href="https://www.finra.org" target="_blank" rel="noopener" class="text-moira hover:underline">FINRA</a> /
          <a href="https://www.sipc.org" target="_blank" rel="noopener" class="text-moira hover:underline">SIPC</a>.
        </p>
        <p class="mb-4">
          By using our Service to open a brokerage account, you will enter into a separate customer agreement
          with Alpaca Securities. You agree to be bound by the terms and conditions of the Alpaca Customer
          Agreement, which is available at
          <a href="https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf" target="_blank" rel="noopener" class="text-moira hover:underline">
            Alpaca Customer Agreement
          </a>.
        </p>
        <p>
          Your brokerage account is protected by SIPC up to $500,000 (including $250,000 for cash claims).
          For more information, visit <a href="https://www.sipc.org" target="_blank" rel="noopener" class="text-moira hover:underline">www.sipc.org</a>.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">4. User Accounts</h2>
        <p class="mb-4">
          When you create an account with us, you must provide accurate, complete, and current information.
          Failure to do so constitutes a breach of the Terms, which may result in immediate termination of
          your account.
        </p>
        <p class="mb-4">
          You are responsible for safeguarding the password that you use to access the Service and for any
          activities or actions under your password. You agree not to disclose your password to any third party.
        </p>
        <p>
          You must notify us immediately upon becoming aware of any breach of security or unauthorized use
          of your account at <a href="mailto:support@moirafinance.com" class="text-moira hover:underline">support@moirafinance.com</a>.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">5. Investment Risks</h2>
        <div class="banner banner-danger mb-4">
          <p class="font-semibold">
            IMPORTANT: Investing in securities involves risks, including the risk of losing some or all of
            your investment. Past performance is not indicative of future results.
          </p>
        </div>
        <p class="mb-3">
          You acknowledge and agree that:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>All investments involve risk of loss</li>
          <li>Past performance does not guarantee future results</li>
          <li>The value of your investments may fluctuate and you may receive back less than you originally invested</li>
          <li>Diversification does not eliminate the risk of investment losses</li>
          <li>Automated investment strategies do not guarantee profits or protection against losses</li>
          <li>You are solely responsible for all investment decisions made through the Service</li>
        </ul>
        <p>
          Before making any investment decisions, you should carefully consider your investment objectives,
          level of experience, and risk tolerance. You should not invest money that you cannot afford to lose.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">6. Automated Investment Features</h2>
        <p class="mb-3">
          The Service provides automated investment features including portfolio allocation ("Pies"),
          automatic rebalancing, and recurring investments. By using these features, you understand and agree that:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Automated features execute based on your configured settings without prior confirmation of each trade</li>
          <li>Market conditions may affect the execution of automated transactions</li>
          <li>You are responsible for monitoring and adjusting your automated settings</li>
          <li>System failures or delays may impact automated transactions</li>
          <li>Tax consequences may result from automated transactions</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">7. Fees and Charges</h2>
        <p class="mb-3">
          Moira Finance currently does not charge fees for basic account services. However, you may incur:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Regulatory transaction fees (SEC fee, TAF fee)</li>
          <li>ACH transfer fees (if applicable)</li>
          <li>Wire transfer fees</li>
          <li>Account transfer fees (ACAT)</li>
          <li>Other fees as disclosed in the Alpaca fee schedule</li>
        </ul>
        <p>
          We reserve the right to introduce or modify fees at any time with prior notice to users.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">8. Prohibited Uses</h2>
        <p class="mb-3">You agree not to use the Service:</p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>For any unlawful purpose or to solicit others to perform illegal acts</li>
          <li>To violate any securities laws, regulations, or rules</li>
          <li>To engage in market manipulation, insider trading, or other fraudulent activities</li>
          <li>To infringe upon or violate our intellectual property rights or those of others</li>
          <li>To harass, abuse, or harm another person</li>
          <li>To submit false or misleading information</li>
          <li>To upload viruses or malicious code</li>
          <li>To attempt to gain unauthorized access to any portion of the Service</li>
          <li>To collect or track the personal information of others</li>
          <li>For any purpose that is prohibited by these Terms</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">9. Intellectual Property</h2>
        <p class="mb-4">
          The Service and its original content, features, and functionality are and will remain the
          exclusive property of Moira Finance and its licensors. The Service is protected by copyright,
          trademark, and other laws of the United States and foreign countries.
        </p>
        <p>
          Our trademarks and trade dress may not be used in connection with any product or service without
          the prior written consent of Moira Finance.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">10. Third-Party Services</h2>
        <p class="mb-4">
          The Service may contain links to third-party websites or services that are not owned or controlled
          by Moira Finance. We have no control over, and assume no responsibility for, the content, privacy
          policies, or practices of any third-party websites or services.
        </p>
        <p>
          You acknowledge and agree that Moira Finance shall not be responsible or liable, directly or
          indirectly, for any damage or loss caused by or in connection with the use of any such content,
          goods, or services available on or through any such websites or services.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">11. Disclaimer of Warranties</h2>
        <p class="mb-4 font-semibold">
          THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. THE SERVICE IS PROVIDED WITHOUT
          WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
        </p>
        <p>
          Moira Finance does not warrant that: (a) the Service will function uninterrupted, secure, or
          available at any particular time or location; (b) any errors or defects will be corrected;
          (c) the Service is free of viruses or other harmful components; or (d) the results of using
          the Service will meet your requirements.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">12. Limitation of Liability</h2>
        <p class="mb-4 font-semibold">
          IN NO EVENT SHALL MOIRA FINANCE, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR
          AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
          INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES,
          RESULTING FROM:
        </p>
        <ul class="list-disc pl-6 space-y-1.5 mb-4">
          <li>Your access to or use of or inability to access or use the Service</li>
          <li>Any conduct or content of any third party on the Service</li>
          <li>Any content obtained from the Service</li>
          <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          <li>Investment losses or other financial damages</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">13. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Moira Finance and its licensees, licensors,
          employees, contractors, agents, officers, and directors from and against any and all claims,
          damages, obligations, losses, liabilities, costs, or debt, and expenses (including but not limited
          to attorney's fees) arising from: (a) your use of and access to the Service; (b) your violation
          of any term of these Terms; (c) your violation of any third-party right, including without
          limitation any copyright, property, or privacy right; or (d) any claim that your use of the
          Service caused damage to a third party.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">14. Dispute Resolution and Arbitration</h2>
        <p class="mb-4">
          Any dispute arising out of or relating to these Terms or the Service shall be resolved through
          binding arbitration in accordance with the rules of the American Arbitration Association.
          The arbitration shall be conducted in [Your State/City], and judgment on the arbitration award
          may be entered into any court having jurisdiction thereof.
        </p>
        <p class="mb-4">
          <strong class="text-ink">Class Action Waiver:</strong> You agree that any arbitration or proceeding shall be
          limited to the dispute between us individually. To the full extent permitted by law: (a) no
          arbitration or proceeding shall be joined with any other; (b) there is no right or authority
          for any dispute to be arbitrated on a class-action basis; and (c) there is no right or authority
          for any dispute to be brought in a purported representative capacity on behalf of the general
          public or any other persons.
        </p>
        <p>
          <strong class="text-ink">Note:</strong> Disputes related to your brokerage account and securities transactions
          are subject to the arbitration provisions in the Alpaca Customer Agreement.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">15. Governing Law</h2>
        <p>
          These Terms shall be governed and construed in accordance with the laws of the United States
          and the State of Delaware, without regard to its conflict of law provisions. Our failure to
          enforce any right or provision of these Terms will not be considered a waiver of those rights.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">16. Changes to Terms</h2>
        <p class="mb-4">
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
          If a revision is material, we will try to provide at least 30 days' notice prior to any new
          terms taking effect. What constitutes a material change will be determined at our sole discretion.
        </p>
        <p>
          By continuing to access or use our Service after those revisions become effective, you agree
          to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">17. Termination</h2>
        <p class="mb-4">
          We may terminate or suspend your account immediately, without prior notice or liability, for
          any reason whatsoever, including without limitation if you breach the Terms. Upon termination,
          your right to use the Service will immediately cease.
        </p>
        <p>
          If you wish to terminate your account, you may simply discontinue using the Service or contact
          us at <a href="mailto:support@moirafinance.com" class="text-moira hover:underline">support@moirafinance.com</a>.
          Note that closing your Moira Finance account does not automatically close your Alpaca brokerage
          account.
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-ink mt-10 mb-3">18. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <div class="card card-pad mt-4">
          <p class="font-semibold text-ink">Moira Finance</p>
          <p>Email: <a href="mailto:support@moirafinance.com" class="text-moira hover:underline">support@moirafinance.com</a></p>
        </div>
      </section>

    </div>
  </main>

  <FooterAdmin />
</div>
